<template>
  <div class="flex flex-col w-full relative gm-automation-v2-node">
    <div class="mb-4 gm-automation-v2-node__step" v-if="headerNode">
      <p class="font-bold text-sm uppercase text-grey-500">
        {{ $t('frontend_groovemail_automations_new_ui_step_label') }}
        {{ stepCount }}
      </p>
    </div>
    <div class="flex items-center gm-automation-v2-node__content" :class="{ 'pl-7 pr-10': !headerNode }">
      <div class="mr-4">
        <component :is="getAutomationFilterNodeIcon"></component>
      </div>
      <div class="flex flex-col min-w-0 gm-automation-v2-node__copy">
        <div class="flex items-center gm-automation-v2-node__label-row">
          <p
            class="text-deep-dark break-all text-sm gm-automation-v2-node__label"
            :class="{ 'font-bold': headerNode }"
          >
            {{
              headerNode
                ? automationFilterNodeHeading
                : autoFiltersData.activity_label
            }}
          </p>
          <p
            class="text-deep-dark ml-2 gm-automation-v2-node__optional"
            v-if="headerNode && type === 'condition'"
          >
            ({{ $t('frontend_groovemail_automations_new_ui_optional_label') }})
          </p>
          <button
            v-if="headerNode"
            class="ml-2"
            v-tooltip="{
              content: automationFilterNodeSubHeading,
              placement: 'right-start',
              classes: ['info'],
              targetClasses: ['it-has-a-tooltip'],
              offset: 10,
              delay: {
                show: 200,
                hide: 300,
              },
            }"
          >
            <InfoIcon class="text-grey-600 h-5 w-5"/>
          </button>
        </div>
        <div>
          <p
            class="text-xs font-bold text-deep-dark ellipse-3-liner gm-automation-v2-node__value"
            v-if="!headerNode"
            :title="automationNodeOptionValue"
          >
            {{ automationNodeOptionValue }}
          </p>
        </div>
      </div>
    </div>
    <FilterIdentifier
      v-if="!headerNode"
      class="absolute z-10 af-filter-identifier gm-automation-v2-node__identifier"
      :filterIdentifier="type"
    />
    <div v-if="!headerNode" class="absolute z-10 right-0 top-0 gm-automation-v2-node__menu">
      <drop-menu
        :menuItems="menuItems"
        dropMenuClass="mt-0 absolute right-0 z-10"
        variant="base"
        small
        smallIcon
      />
    </div>
  </div>
</template>

<script>
import DropMenu from '@groovepages/gd-ui-kit/components/DropMenu';
import DropdownIcon from '@groovepages/gd-ui-kit/assets/dropdown-dots.svg';
import FilterIdentifier from '@/components/automations-v2/filter-identifier.vue';
import logsIconsMixin from '@/pages/mail_lead/logs-icons-mixin';

// import icons
import InfoIcon from '@/assets/images/icons/new/info_new.svg';
import TriggersIcon from '@/assets/images/icons/TriggersIcon.svg';
import ConditionsIcon from '@/assets/images/icons/ConditionsIcon.svg';
import ActionsIcon from '@/assets/images/icons/ActionsIcon.svg';
import DefaultActivityIcon from '@/assets/images/icons/default-activity-icon.svg';

export default {
  name: 'automation-node',
  mixins: [logsIconsMixin],

  props: {
    headerNode: {
      required: false,
      default: false,
    },
    automationFiltersData: {
      required: false,
      default: null,
    },
    type: {
      required: true,
    },
    autoFilterIndex: {
      required: false,
    },
    stepCount: {
      required: false,
      default: 1,
    },
  },

  components: {
    DropMenu,
    DropdownIcon,
    FilterIdentifier,
    TriggersIcon,
    ConditionsIcon,
    ActionsIcon,
    InfoIcon,
    DefaultActivityIcon,
  },

  data() {
    return {
      autoFiltersData: { ...this.automationFiltersData },
    };
  },

  computed: {
    dropMenuIcon() {
      return DropdownIcon;
    },

    automationFilterNodeHeading() {
      let filterNodeHeading = '';
      if (this.type === 'trigger') {
        filterNodeHeading = this.$t(
          'frontend_groovemail_sequence_analytics_triggers'
        );
      } else if (this.type === 'condition') {
        filterNodeHeading = this.$t('frontend_conditions');
      } else {
        filterNodeHeading = this.$t('frontend_actions');
      }
      return filterNodeHeading;
    },

    automationFilterNodeSubHeading() {
      let filterNodeHeading = '';
      if (this.type === 'trigger') {
        filterNodeHeading = this.$t(
          'frontend_groovemail_automations_filter_node_trigger_sub_heading'
        );
      } else if (this.type === 'condition') {
        filterNodeHeading = this.$t(
          'frontend_groovemail_automations_filter_node_condition_sub_heading'
        );
      } else {
        filterNodeHeading = this.$t(
          'frontend_groovemail_automations_filter_node_action_sub_heading'
        );
      }
      return filterNodeHeading;
    },

    getAutomationFilterNodeIcon() {
      let filterIcon = '';
      if (this.automationFiltersData && this.automationFiltersData.icon) {
        filterIcon = this.automationFiltersData.icon;
        if (!this.componentMapping[filterIcon]) {
          filterIcon = DefaultActivityIcon;
        }
      } else if (this.type === 'trigger') {
        filterIcon = TriggersIcon;
      } else if (this.type === 'condition') {
        filterIcon = ConditionsIcon;
      } else {
        filterIcon = ActionsIcon;
      }
      return filterIcon;
    },

    automationNodeOptionValue() {
      let nodeValue = '';
      if (this.autoFiltersData && this.autoFiltersData.config_name) {
        const keys = Object.keys(this.autoFiltersData.config_name);
        keys.forEach((key, index) => {
          if (index === keys.length - 1) {
            nodeValue += `${this.autoFiltersData.config_name[key]}`;
          } else {
            nodeValue += `${this.autoFiltersData.config_name[key]}, `;
          }
        });
      }
      return nodeValue;
    },

    menuItems() {
      const menuItems = [];
      menuItems.push({
        text: this.$t('frontend_edit'),
        callback: () => this.$emit('editAutomationNode', this.autoFilterIndex),
      });
      if (this.type !== 'trigger') {
        menuItems.push({
          text: this.$t('frontend_delete'),
          callback: () =>
            this.$emit('deleteAutomationNode', this.autoFilterIndex),
        });
      }
      return menuItems;
    },
  },
};
</script>

<style lang="scss">

.ellipse-3-liner {
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}
</style>

<style lang="scss" scoped>
.gm-automation-v2-node {
  color: var(--gs-foreground);
  &__content { min-width: 0; }
  &__copy { flex: 1 1 auto; overflow-wrap: anywhere; }
  &__label-row { flex-wrap: wrap; column-gap: 0.5rem; row-gap: 0.25rem; }
  &__label,
  &__optional {
    min-width: 0;
    word-break: normal !important;
    overflow-wrap: anywhere;
  }
  &__optional { margin-left: 0 !important; }
  &__value {
    word-break: normal;
    overflow-wrap: anywhere;
  }
  &__menu { width: 2.75rem; height: 2.75rem; }
  ::v-deep .gm-automation-v2-node__menu .relative.select-none { width: 2.75rem !important; height: 2.75rem !important; display: flex; }
  ::v-deep .gm-automation-v2-node__menu .relative.select-none > div { width: 100%; height: 100%; min-width: 2.75rem; min-height: 2.75rem; display: flex; align-items: center; justify-content: center; border-radius: var(--gs-radius-sm); }
  ::v-deep .gm-automation-v2-node__menu .drop-menu-item { min-height: 2.75rem; display: flex; align-items: center; }
  @media (max-width: 40rem) {
    &__content { align-items: flex-start; padding-right: 3.5rem !important; }
    &__step { margin-bottom: 0.5rem; }
    &__menu { top: -0.25rem; }
  }
}
</style>
